import { ChangeEvent, FormEvent, useCallback, useEffect, useRef } from "react";
import {
  Chat,
  ChatDrawer,
  ChatProvider as FungiProvider,
  MessageRatingValue,
  useChatContext,
} from "@evg-ui/fungi";
import { post } from "@evg-ui/lib/utils/request/post";
import { useAIAgentAnalytics } from "analytics";
import { aiPrompts } from "constants/aiPrompts";
import { useLogContext } from "context/LogContext";
import { parsleyAIURL } from "utils/environmentVariables";

const chatURL = `${parsleyAIURL}/completions/parsley/conversations/chat`;
const ratingURL = `${parsleyAIURL}/completions/parsley/conversations/rate`;
const loginURL = `${parsleyAIURL}/login`;

export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <FungiProvider appName="Parsley AI">{children}</FungiProvider>;

export const Chatbot: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { sendEvent } = useAIAgentAnalytics();
  const { logMetadata, openSectionAndScrollToLine } = useLogContext();
  const { drawerOpen } = useChatContext();
  const { execution, fileName, groupID, logType, origin, taskID, testID } =
    logMetadata ?? {};

  const bodyData = {
    logMetadata: {
      execution: Number(execution),
      fileName: fileName,
      group_id: groupID,
      log_type: logType,
      origin,
      task_id: taskID,
      test_id: testID,
    },
  };

  const handleFeedback = useCallback(
    (spanId: string) =>
      async (
        _e: FormEvent<HTMLFormElement> | ChangeEvent<HTMLInputElement>,
        options?: { feedback?: string; rating: MessageRatingValue },
      ) => {
        // This should never happen but LG's handler is oddly typed
        if (!options) return;

        // Track when feedback is submitted (has feedback text) vs just rating
        if (options.feedback) {
          sendEvent({
            feedback: options.feedback,
            name: "Clicked submit feedback button",
            spanId,
          });
        }
        sendEvent({
          name: "Clicked submit rating button",
          rating: options.rating,
          spanId,
        });

        const handleError = (err: Error) => {
          // Re-throw error to invoke LG error state
          throw err;
        };
        await post(
          ratingURL,
          {
            // Clicking thumb icon will omit `feedback`, but otherwise rating and providing feedback do the same thing.
            feedback: options?.feedback,
            rating: options.rating === MessageRatingValue.Liked ? 1 : 0,
            spanId,
          },
          handleError,
        );
      },
    [sendEvent],
  );

  const handleCopy = useCallback(() => {
    sendEvent({ name: "Clicked copy response button" });
  }, [sendEvent]);

  // Don't send action event on mount, but rather when it changes after the default is set
  const isInitialRender = useRef(true);
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    sendEvent({
      name: "Toggled AI agent panel",
      open: drawerOpen,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerOpen]);

  return (
    <ChatDrawer
      chatContent={
        <Chat
          apiUrl={chatURL}
          bodyData={bodyData}
          chatSuggestions={aiPrompts}
          handleRatingChange={handleFeedback}
          handleSubmitFeedback={handleFeedback}
          loginUrl={loginURL}
          onChipClick={(chip) => {
            const lineNumber = chip.metadata?.startingLine;
            if (typeof lineNumber === "number") {
              openSectionAndScrollToLine(lineNumber);
            }
          }}
          onClickCopy={handleCopy}
          onClickSuggestion={(suggestion) => {
            sendEvent({ name: "Clicked suggestion", suggestion });
          }}
          onLinkClick={(href) => {
            const line = parseInt(href.replace("#L", ""), 10);
            if (!Number.isNaN(line)) {
              openSectionAndScrollToLine(line);
            }
          }}
          onSendMessage={(_message, { conversationId, messageIndex }) => {
            if (messageIndex === 0) {
              sendEvent({
                "conversation.id": conversationId,
                name: "Created Parsley AI session",
              });
            }
          }}
          transformMessage={(message, { pendingChips: chips }) => {
            let transformed = message;
            if (chips && chips.length > 0) {
              const contextText = chips
                .map((chip) => `[${chip.badgeLabel}]: ${chip.content}`)
                .join("\n");
              transformed = `${message}\nThe user also supplied the following lines as context for their query:\n${contextText}`;
            }
            return transformed;
          }}
        />
      }
      data-testid="chat-drawer"
    >
      {children}
    </ChatDrawer>
  );
};
